import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  makeStyles,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ZoomIn as ZoomInIcon,
  PhotoCamera as PhotoCameraIcon
} from '@material-ui/icons';
import { UserContext } from '../../../context/UserProvider';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0, // Remover padding para mejor integración
  },
  galleryContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  imageCard: {
    width: 200,
    height: 200,
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  image: {
    height: 150,
    objectFit: 'cover',
  },
  expandedImage: {
    maxWidth: '100%',
    maxHeight: '80vh',
    objectFit: 'contain',
  },
  filterContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  searchField: {
    minWidth: 200,
  },
  cropChip: {
    margin: theme.spacing(0.5),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  imageInfo: {
    padding: theme.spacing(1),
  },
  expandedDialog: {
    '& .MuiDialog-paper': {
      maxWidth: '90vw',
      maxHeight: '90vh',
    },
  },
  imageMetadata: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
}));

export const CameraImages = ({ selectedCropName = null }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { usuario } = React.useContext(UserContext);
  
  // Estados principales
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados para filtros
  const [selectedCrop, setSelectedCrop] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  
  // Estados para imagen expandida
  const [expandedImage, setExpandedImage] = useState(null);
  const [expandedOpen, setExpandedOpen] = useState(false);
  
  // Cloud Function endpoint (ajusta según tu endpoint real)
  const CLOUD_FUNCTION_ENDPOINT = 'https://us-central1-gapy-c999c.cloudfunctions.net/getCameraImages';

  // Función para obtener imágenes desde Cloud Function
  const fetchImages = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(CLOUD_FUNCTION_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: usuario.username,
          // Puedes agregar más parámetros según tu Cloud Function
        }),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Simular datos de ejemplo si la Cloud Function no está disponible
      const mockImages = data.images || generateMockImages();
      setImages(mockImages);
      setFilteredImages(mockImages);
      
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Error al cargar las imágenes: ' + err.message);
      
      // Usar datos mock en caso de error
      const mockImages = generateMockImages();
      setImages(mockImages);
      setFilteredImages(mockImages);
    } finally {
      setLoading(false);
    }
  };

  // Generar imágenes de ejemplo para testing
  const generateMockImages = () => {
    const crops = ['Lechuga', 'Tomate', 'Chile', 'Pepino', 'Frambuesa'];
    const mockImages = [];
    
    for (let i = 1; i <= 20; i++) {
      // Si hay un cultivo seleccionado, usar ese cultivo para la mayoría de imágenes
      const crop = selectedCropName && i <= 15 ? selectedCropName : crops[Math.floor(Math.random() * crops.length)];
      const date = new Date();
      date.setDate(date.getDate() - Math.floor(Math.random() * 30));
      
      mockImages.push({
        id: `img_${i}`,
        url: `https://picsum.photos/400/300?random=${i}`,
        thumbnail: `https://picsum.photos/200/150?random=${i}`,
        crop: crop,
        date: date.toISOString().split('T')[0],
        timestamp: date.toISOString(),
        description: `Imagen de ${crop} - ${date.toLocaleDateString()}`,
        metadata: {
          resolution: '1920x1080',
          size: `${Math.floor(Math.random() * 500) + 100}KB`,
          camera: 'Camera_001',
        }
      });
    }
    
    return mockImages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  // Aplicar filtros
  useEffect(() => {
    let filtered = [...images];

    // Filtro por cultivo
    if (selectedCrop) {
      filtered = filtered.filter(img => img.crop === selectedCrop);
    }

    // Filtro por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(img => 
        img.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        img.crop.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por fecha
    if (dateFilter) {
      filtered = filtered.filter(img => img.date === dateFilter);
    }

    setFilteredImages(filtered);
  }, [images, selectedCrop, searchTerm, dateFilter]);

  // Manejar apertura del modal
  const handleOpen = () => {
    setOpen(true);
    if (images.length === 0) {
      fetchImages();
    }
    // Si hay un cultivo seleccionado, filtrar automáticamente por ese cultivo
    if (selectedCropName) {
      setSelectedCrop(selectedCropName);
    }
  };

  // Manejar cierre del modal
  const handleClose = () => {
    setOpen(false);
    setExpandedOpen(false);
    setExpandedImage(null);
  };

  // Manejar clic en imagen para expandir
  const handleImageClick = (image) => {
    setExpandedImage(image);
    setExpandedOpen(true);
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSelectedCrop('');
    setSearchTerm('');
    setDateFilter('');
  };

  // Obtener cultivos únicos para el filtro
  const uniqueCrops = [...new Set(images.map(img => img.crop))];

  return (
    <div className={classes.root}>
      <Button
        variant="contained"
        color="primary"
        startIcon={<PhotoCameraIcon />}
        onClick={handleOpen}
        style={{
          borderRadius: 10,
          padding: '12px 24px',
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
          '&:hover': {
            background: 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)',
          }
        }}
      >
        <span role="img" aria-label="camera">📸</span> Ver Imágenes de Cámaras
      </Button>

      {/* Modal principal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1)
        }}>
          <PhotoCameraIcon />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Galería de Imágenes de Cámaras
          </Typography>
          <Box style={{ marginLeft: 'auto' }}>
            <IconButton onClick={handleClose} style={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Filtros */}
          <Box className={classes.filterContainer}>
            <TextField
              className={classes.searchField}
              placeholder="Buscar imágenes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />

            <FormControl size="small" style={{ minWidth: 150 }}>
              <InputLabel>Cultivo</InputLabel>
              <Select
                value={selectedCrop}
                onChange={(e) => setSelectedCrop(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {uniqueCrops.map((crop) => (
                  <MenuItem key={crop} value={crop}>
                    {crop}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              type="date"
              label="Fecha"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />

            <Button
              startIcon={<FilterIcon />}
              onClick={clearFilters}
              size="small"
            >
              Limpiar
            </Button>
          </Box>

          {/* Chips de cultivos activos */}
          {selectedCrop && (
            <Box mb={2}>
              <Chip
                label={`${selectedCrop} (${filteredImages.length})`}
                onDelete={() => setSelectedCrop('')}
                color="primary"
                className={classes.cropChip}
              />
            </Box>
          )}

          {/* Contenido de la galería */}
          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" className={classes.errorAlert}>
              {error}
            </Alert>
          ) : filteredImages.length === 0 ? (
            <Box className={classes.emptyState}>
              <PhotoCameraIcon style={{ fontSize: 64, color: 'gray' }} />
              <Typography variant="h6" gutterBottom>
                No se encontraron imágenes
              </Typography>
              <Typography variant="body2">
                {images.length === 0 
                  ? 'No hay imágenes disponibles' 
                  : 'No hay imágenes que coincidan con los filtros aplicados'
                }
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2} className={classes.galleryContainer}>
              {filteredImages.map((image) => (
                <Grid item key={image.id}>
                  <Card 
                    className={classes.imageCard}
                    onClick={() => handleImageClick(image)}
                  >
                    <CardMedia
                      className={classes.image}
                      image={image.thumbnail}
                      title={image.description}
                    />
                    <CardContent className={classes.imageInfo}>
                      <Typography variant="body2" noWrap>
                        {image.crop}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(image.timestamp).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para imagen expandida */}
      <Dialog
        open={expandedOpen}
        onClose={() => setExpandedOpen(false)}
        maxWidth="md"
        fullWidth
        className={classes.expandedDialog}
      >
        {expandedImage && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  {expandedImage.crop} - {new Date(expandedImage.timestamp).toLocaleDateString()}
                </Typography>
                <IconButton onClick={() => setExpandedOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            
            <DialogContent>
              <Box textAlign="center">
                <img
                  src={expandedImage.url}
                  alt={expandedImage.description}
                  className={classes.expandedImage}
                />
                
                <Box className={classes.imageMetadata}>
                  <Typography variant="h6" gutterBottom>
                    Información de la Imagen
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Cultivo:</strong> {expandedImage.crop}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Fecha:</strong> {new Date(expandedImage.timestamp).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Resolución:</strong> {expandedImage.metadata.resolution}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Tamaño:</strong> {expandedImage.metadata.size}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Descripción:</strong> {expandedImage.description}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </DialogContent>
            
            <DialogActions>
              <Button 
                startIcon={<ZoomInIcon />}
                onClick={() => window.open(expandedImage.url, '_blank')}
                color="primary"
              >
                Ver en nueva pestaña
              </Button>
              <Button onClick={() => setExpandedOpen(false)}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};
